import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { safeError } from '@/lib/safeError';

type MfaRequirement = 'none' | 'setup' | 'verify';

interface AdminMfaGuardResult {
  /** Whether the check is still loading */
  loading: boolean;
  /** What MFA action the admin must complete before proceeding */
  mfaRequired: MfaRequirement;
  /** The factor ID to verify against (only set when mfaRequired === 'verify') */
  factorId: string;
}

/**
 * Global guard that checks whether the current user is an admin who needs
 * to complete MFA before accessing the app.
 *
 * - If the user is not an admin, returns mfaRequired: 'none' immediately.
 * - If the user is an admin without any enrolled TOTP factor, returns 'setup'.
 * - If the user is an admin with an enrolled factor but the session is aal1
 *   (password-only), returns 'verify'.
 * - If the user is an admin with aal2, returns 'none'.
 */
export function useAdminMfaGuard(): AdminMfaGuardResult {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState<MfaRequirement>('none');
  const [factorId, setFactorId] = useState('');
  const checkedRef = useRef<string | null>(null);

  useEffect(() => {
    const check = async () => {
      if (!user || !session) {
        setMfaRequired('none');
        setLoading(false);
        return;
      }

      // Avoid re-running for the same user within this mount
      if (checkedRef.current === user.id) return;
      checkedRef.current = user.id;

      try {
        // 1. Is the user an admin?
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .in('role', ['admin', 'superadmin'])
          .maybeSingle();

        if (!roleData) {
          // Not an admin — no MFA requirement
          setMfaRequired('none');
          setLoading(false);
          return;
        }

        // 2. Check current AAL and enrolled factors
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        const { data: factorsData } = await supabase.auth.mfa.listFactors();

        // Primary: use listFactors() typed arrays
        let verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') ?? [];

        // Fallback: if listFactors().totp is empty, check user.factors directly
        // (covers race conditions where getUser() inside listFactors returns stale data)
        if (verifiedFactors.length === 0) {
          const userFactors = (user as any)?.factors ?? [];
          const fromUser = userFactors.filter(
            (f: any) => f.factor_type === 'totp' && f.status === 'verified'
          );
          if (fromUser.length > 0) {
            verifiedFactors = fromUser;
          }
        }

        const currentLevel = aalData?.currentLevel;

        if (verifiedFactors.length === 0) {
          // Admin has no TOTP factor — must set one up
          setMfaRequired('setup');
        } else if (currentLevel !== 'aal2') {
          // Admin has a factor but session hasn't verified it yet
          setFactorId(verifiedFactors[0].id);
          setMfaRequired('verify');
        } else {
          // Admin with aal2 — all good
          setMfaRequired('none');
        }
      } catch (err) {
        console.error('[useAdminMfaGuard] Error checking MFA status:', safeError(err));
        // Fail open to avoid locking admins out on transient errors,
        // but the login-page check remains as a safety net.
        setMfaRequired('none');
      } finally {
        setLoading(false);
      }
    };

    check();
  }, [user, session]);

  return { loading, mfaRequired, factorId };
}
